import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { _post } from "../../../utils/network";
import Breadcrumb from "../../../components/Breadcrumb/Breadcrumb.component";
import LoadingButton from "../../../components/LoadingButton/LoadingButton.component";

const ASSET_FIELDS = [
	{ key: "coin_count", title: "coin_count" },
	{ key: "gold_bar_count", title: "gold_bar_count" },
	{ key: "bank_deposit", title: "bank_deposit" },
	{ key: "other_assets", title: "other_assets" },
];

const GoldFundsList = () => {
  const { t } = useTranslation();
  const [funds, setFunds] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingIsin, setSavingIsin] = useState(null);

  const getFunds = useCallback(async () => {
    setLoading(true);
    const response = await _post("/goldfunds/guest/getAll", {});
    setLoading(false);
    if (response?.code !== 200) {
      toast.error(
        response?.farsi_message || t("gold_funds_fetch_failed"),
      );
      return;
    }
    const items = response?.data?.gold_funds || [];
    setFunds(items);
    setDrafts(
      Object.fromEntries(
        items.map((fund) => [
          fund.isin,
          Object.fromEntries(
            ASSET_FIELDS.map((field) => [field.key, fund[field.key] ?? 0]),
          ),
        ]),
      ),
    );
  }, [t]);

  useEffect(() => {
    getFunds();
  }, [getFunds]);

  const setValue = (isin, key, value) => {
    setDrafts((current) => ({
      ...current,
      [isin]: { ...current[isin], [key]: value },
    }));
  };

  const saveAssets = async (isin) => {
    if (savingIsin) return;
    const body = { isin, ...drafts[isin] };
    if (
      ASSET_FIELDS.some(
        (field) =>
          body[field.key] === "" ||
          Number(body[field.key]) < 0 ||
          !Number.isFinite(Number(body[field.key])),
      )
    ) {
      toast.warning(t("gold_funds_asset_values_invalid"));
      return;
    }
    setSavingIsin(isin);
    const response = await _post("/goldfunds/admin/updateAssets", body);
    setSavingIsin(null);
    if (response?.code === 200) {
      toast.success(t("gold_funds_assets_saved"));
      getFunds();
    } else {
      toast.error(response?.farsi_message || t("gold_funds_assets_save_failed"));
    }
  };

  const breadcrumbItems = [
    { title: t("dashboard"), link: "/dashboard" },
    { title: t("gold_funds") },
  ];

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />
      <div className="card mb-3">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="mb-0">{t("gold_funds")}</h5>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={getFunds}
            disabled={loading}
          >
            {t("update")}
          </button>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4 text-center">{t("loading_gold_funds")}</div>
          ) : funds.length === 0 ? (
            <div className="p-4 text-center">{t("no_gold_funds")}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>{t("gold_fund_symbol")}</th>
                    <th>{t("isin")}</th>
                    {ASSET_FIELDS.map((field) => (
                      <th key={field.key}>{t(field.title)}</th>
                    ))}
                    <th>{t("operation")}</th>
                  </tr>
                </thead>
                <tbody>
                  {funds.map((fund) => (
                    <tr key={fund.isin}>
                      <td className="fw-bold">{fund.symbol}</td>
                      <td dir="ltr">{fund.isin}</td>
                      {ASSET_FIELDS.map((field) => (
                        <td key={field.key} style={{ minWidth: 130 }}>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="form-control form-control-sm"
                            value={drafts[fund.isin]?.[field.key] ?? ""}
                            onChange={(event) =>
                              setValue(fund.isin, field.key, event.target.value)
                            }
                            aria-label={`${fund.symbol} ${t(field.title)}`}
                          />
                        </td>
                      ))}
                      <td>
                        <LoadingButton
                          className="btn btn-primary btn-sm text-nowrap"
                          type="button"
                          isLoading={savingIsin === fund.isin}
                          onClick={() => saveAssets(fund.isin)}
                          disabled={Boolean(
                            savingIsin && savingIsin !== fund.isin,
                          )}
                        >
                          <i className="fa fa-save"></i> {t("update")}
                        </LoadingButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GoldFundsList;
